import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiDroplet, FiAward } = FiIcons;

const Services = () => {
  const services = [
    {
      id: 'cleaning',
      icon: FiDroplet,
      title: 'Reinigungsmanagement',
      description: 'Professionelle Reinigungsdienste für alle Arten von Gebäuden und Anlagen.',
      image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      features: [
        'Büro- und Geschäftsreinigung',
        'Industriereinigung',
        'Glasreinigung',
        'Teppichreinigung',
        'Spezialreinigung',
        'Desinfektion'
      ],
      benefits: [
        'Individuelle Reinigungspläne',
        'Qualifiziertes Personal',
        'Umweltfreundliche Produkte',
        'Flexible Zeiten'
      ]
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Bedarfsanalyse',
      description: 'Wir analysieren Ihre Anforderungen und erstellen ein maßgeschneidertes Konzept.'
    },
    {
      step: '02',
      title: 'Angebot',
      description: 'Sie erhalten ein transparentes und detailliertes Angebot für alle Leistungen.'
    },
    {
      step: '03',
      title: 'Umsetzung',
      description: 'Unser erfahrenes Team setzt die vereinbarten Services zuverlässig um.'
    },
    {
      step: '04',
      title: 'Qualitätskontrolle',
      description: 'Regelmäßige Kontrollen stellen sicher, dass unsere hohen Standards eingehalten werden.'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-16 sm:py-20 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Unsere Leistungen</h1>
            <p className="text-lg sm:text-xl max-w-3xl mx-auto">
              Professionelle Facility Management Lösungen aus einer Hand. Wir kümmern uns um alles, damit Sie sich auf Ihr Kerngeschäft konzentrieren können.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail - Beautiful Single-Column Layout */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center w-full">
            {services.map((service) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden w-full max-w-5xl border border-gray-100"
              >
                <div className="relative h-64 sm:h-80 lg:h-96 w-full">
                  <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 p-8 sm:p-12 flex items-end">
                    <SafeIcon icon={service.icon} className="w-12 h-12 text-orange-400 mr-6 mb-2 hidden sm:block" />
                    <div>
                      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">{service.title}</h2>
                      <div className="w-20 h-1.5 bg-orange-500 rounded-full"></div>
                    </div>
                  </div>
                </div>

                <div className="p-8 sm:p-12">
                  <p className="text-lg sm:text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto leading-relaxed">
                    {service.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                    <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
                      <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-orange-500 mr-3" />
                        Leistungsumfang
                      </h3>
                      <ul className="space-y-4">
                        {service.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start">
                            <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-gray-700 text-lg">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-orange-50 p-8 rounded-2xl border border-orange-100">
                      <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <SafeIcon icon={FiAward} className="w-6 h-6 text-orange-500 mr-3" />
                        Ihre Vorteile
                      </h3>
                      <ul className="space-y-4">
                        {service.benefits.map((benefit, benefitIndex) => (
                          <li key={benefitIndex} className="flex items-start">
                            <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-gray-800 font-medium text-lg">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Unser Vorgehen</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              In vier einfachen Schritten zu Ihrem optimalen Facility Management
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-orange-500 text-white w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold mx-auto mb-4 sm:mb-6">
                  {step.step}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;